import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from '@material-ui/core/IconButton';
import AsyncTaskWrapper from 'CMFW/input/AsyncTaskWrapper';
import ModuleContext from 'CMFW/context/ModuleContext';
import { SelectLayout } from 'components/selects/SelectLayout';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { IAsyncTask } from 'types/asyncTask';
import VocabularyModel from '../../models/VocabularyModel';

const KEY = 'level';
const TASK_KEY = 'levelTask';
const UNASSIGNED = -1;

type ILevel = {
  value: number;
  label: string;
};

// Course level of the concept (Voc.level). 0 and 9 are not study levels.
const options: ILevel[] = [
  { value: 1, label: 'A1.1' },
  { value: 2, label: 'A1.2' },
  { value: 3, label: 'A2' },
  { value: 4, label: 'B1' },
  { value: 5, label: 'B2' },
  { value: 6, label: 'C1+' },
  { value: 0, label: 'Free use' },
  { value: 9, label: 'Out of courses' },
];

const unassigned: ILevel = { value: UNASSIGNED, label: 'Unassigned' };

const toValue = (level: number | null | undefined): number =>
  level === null || level === undefined ? UNASSIGNED : level;

const LevelSelect: React.FC = () => {
  const module = useContext(ModuleContext);
  const moduleItemValue = toValue(module.getItemValue(KEY) as number | null);
  const levelTask = module.getItemValue(TASK_KEY) as IAsyncTask<number | null> | undefined;

  // source of truth = the edited item, so a level set by the async task shows up when it lands
  const currentValue = toValue(module.getItemUpdatedValue(KEY) as number | null);

  // «Unassigned» is shown while the card has no level, never offered as a choice
  const data = currentValue === UNASSIGNED ? [unassigned, ...options] : options;

  const handleChange = (level: ILevel) => {
    if (level.value === UNASSIGNED) return;
    module.updateItemValue.bind(module)(KEY, level.value);
  };

  const handleRecalculate = async () => {
    await (module as VocabularyModel).recalculateLevel();
  };

  const select = (
    <SelectLayoutStyled
      data={data}
      onChange={handleChange}
      label="Level"
      optionLabelKey="label"
      optionValueKey="value"
      currentValue={currentValue}
      originalValue={moduleItemValue}
    />
  );

  return (
    <Wrapper>
      {levelTask ? (
        <AsyncTaskWrapper task={levelTask} keyValue={KEY}>
          {select}
        </AsyncTaskWrapper>
      ) : (
        select
      )}
      {module.getItemId() > 0 && (
        <IconButton size="small" onClick={handleRecalculate} title="Recalculate level">
          <FontAwesomeIcon icon={faRedo} size="sm" />
        </IconButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 0;
`;

const SelectLayoutStyled = styled(SelectLayout)`
  width: 130px;
  flex-grow: 0;
`;

export default LevelSelect;
